/**
 * useMessages Hook
 * React hook for managing conversations with real API integration
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { APIError } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://onlyjalen-api.jalen1wa.workers.dev';

interface Conversation {
  partner_id: string;
  partner_name: string;
  partner_avatar: string;
  partner_email: string;
  last_message: string;
  last_message_at: string;
  unread_count: number;
  /** ISO timestamp of partner's last authenticated request. */
  partner_last_seen?: string | null;
  /** Server-derived presence (1 if last_seen < 90s ago). Authoritative —
   *  client also has utils/presence.isOnline() as a fallback derivation. */
  partner_is_online?: 0 | 1 | boolean;
}

interface Message {
  id: string;
  from_user_id: string;
  to_user_id: string;
  content: string;
  read: boolean;
  created_at: string;
  sender_name?: string;
  sender_avatar?: string;
  sender_role?: 'admin' | 'subscriber';
}

interface UseMessagesReturn {
  conversations: Conversation[];
  selectedMessages: Message[];
  loading: boolean;
  error: string | null;
  unreadCount: number;
  refresh: () => Promise<void>;
  loadConversation: (partnerId: string) => Promise<void>;
  sendMessage: (partnerId: string, content: string) => Promise<boolean>;
  /** Optimistically clear unread_count for a conversation locally (and
   *  POST to the server). Call this when the user opens a conversation
   *  so the inbox badge updates immediately. */
  markConversationRead: (partnerId: string) => Promise<void>;
  isSending: boolean;
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('onlyjalen_access_token');

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new APIError(data.error || 'API request failed', response.status);
  }

  return response.json();
}

export function useMessages(): UseMessagesReturn {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedMessages, setSelectedMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const sendRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<{ conversations: Conversation[] }>('/api/messages/conversations');
      setConversations(data.conversations || []);
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to load conversations';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Poll every 30s so a new inbound message surfaces in the inbox
    // without manual refresh. Matches useAdminStats cadence.
    const id = setInterval(() => { refresh(); }, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const loadConversation = useCallback(async (partnerId: string) => {
    setLoading(true);
    try {
      const data = await apiRequest<{ messages: Message[] }>(`/api/messages/conversations/${partnerId}`);
      setSelectedMessages(data.messages || []);
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to load messages';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMessage = useCallback(async (partnerId: string, content: string): Promise<boolean> => {
    if (sendRef.current || !content.trim()) return false;
    sendRef.current = true;
    setIsSending(true);

    try {
      const data = await apiRequest<{ success: boolean; message: Message }>(`/api/messages/conversations/${partnerId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: content.trim() }),
      });

      if (data.success && data.message) {
        setSelectedMessages(prev => [...prev, data.message]);
        // Refresh conversations to update last message
        await refresh();
        return true;
      }
      return false;
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to send message';
      setError(message);
      return false;
    } finally {
      setIsSending(false);
      sendRef.current = false;
    }
  }, [refresh]);

  // Note: a previous markAsRead helper here called PATCH /api/messages/:id/read
  // which never existed as a backend route. Removed 2026-05-17 to avoid a
  // footgun for future callers — use markConversationRead instead, which hits
  // the real POST /api/messages/conversations/:partnerId/read endpoint.

  const markConversationRead = useCallback(async (partnerId: string) => {
    // 1. Optimistic local update — the inbox unread badge clears the
    //    moment the user taps a conversation, not after a refetch.
    setConversations(prev => prev.map(c =>
      c.partner_id === partnerId ? { ...c, unread_count: 0 } : c
    ));
    // 2. Persist server-side. RealtimeChatPanel also fires markAsRead
    //    via WebSocket on open, but doing it here too is safe and
    //    closes the race when the WS isn't connected yet.
    try {
      await apiRequest(`/api/messages/conversations/${partnerId}/read`, { method: 'POST' });
    } catch {
      /* silent — local state is already correct */
    }
  }, []);

  const unreadCount = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  return {
    conversations,
    selectedMessages,
    loading,
    error,
    unreadCount,
    refresh,
    loadConversation,
    sendMessage,
    markConversationRead,
    isSending,
  };
}

export default useMessages;
